/**
 * Toast Notification Store.
 *
 * Global state for managing toast notifications. Toasts are stacked
 * bottom-right per Apex §8.7 with auto-dismiss timers based on type.
 */

import { create } from "zustand";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  action?: { label: string; onClick: () => void };
  duration: number;
  createdAt: number;
}

interface ToastState {
  toasts: Toast[];
  addToast: (
    toast: Omit<Toast, "id" | "createdAt" | "duration"> & { duration?: number },
  ) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

let counter = 0;

/** Default auto-dismiss durations per §8.7 */
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 0, // No auto-dismiss for errors.
};

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = `toast-${++counter}-${Date.now()}`;
    const duration = toast.duration ?? DEFAULT_DURATIONS[toast.type];

    set((state) => ({
      toasts: [
        ...state.toasts,
        { ...toast, id, duration, createdAt: Date.now() },
      ],
    }));

    // Auto-dismiss (error toasts stay until manually closed).
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearAll: () => set({ toasts: [] }),
}));

/** Convenience functions for common toast types. */
export const toast = {
  success: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: "success", title, message }),
  error: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: "error", title, message }),
  warning: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: "warning", title, message }),
  info: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: "info", title, message }),
};
